import {
  readRoster,
  type CustomRosterPlayerCount,
} from '@repo/sdk'
import type { ReactNode } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { expandWolfSiblingChoice } from '@/lib/wolf-sibling-configuration'

interface BrawlRosterConfigurationProps {
  playerCount: CustomRosterPlayerCount
  wolfSiblingPublicEnabled: boolean
  showModels: boolean
  t: (key: string) => string
  roleName: (key: string) => string
  seatName: (key: string) => string
  renderModel: (key: string, enabled: boolean) => ReactNode
}

export function BrawlRosterConfiguration({
  playerCount,
  wolfSiblingPublicEnabled,
  showModels,
  t,
  roleName,
  seatName,
  renderModel,
}: BrawlRosterConfigurationProps) {
  const { choices } = readRoster({ kind: 'catalog', wolfSiblings: wolfSiblingPublicEnabled })
  const gridClass = `grid gap-3 pl-2 ${showModels ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`
  const fixedSeat = (key: string) => (
    <div key={key} className="flex flex-wrap items-center gap-1.5" data-testid={`brawl-fixed-${key}`}>
      <Checkbox id={`brawl-fixed-${key}`} checked disabled className="border-gray-400 data-[state=checked]:bg-gray-400 data-[state=checked]:text-white" />
      <Label htmlFor={`brawl-fixed-${key}`} className="text-sm font-normal !opacity-100">
        {seatName(key)}
      </Label>
      {renderModel(key, true)}
    </div>
  )
  const randomSlot = (kind: string, keys: readonly string[]) => (
    <div className={gridClass} data-testid={`brawl-random-${kind}`}>
      {keys.map(key => (
        <div key={key} className="contents" data-testid={`brawl-candidate-${key}`}>
          {expandWolfSiblingChoice(key).map(seat => (
            <div key={seat} className="flex flex-wrap items-center gap-1.5" data-testid={seat === key ? undefined : `brawl-candidate-${seat}`}>
              <div className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-muted" aria-hidden="true">
                <span className="text-xs font-bold text-muted-foreground">?</span>
              </div>
              <span className="text-sm">{roleName(seat)}</span>
              {renderModel(seat, true)}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
  const wolfCount = playerCount === 12 ? 4 : 3
  const villagerCount = playerCount === 12 ? 4 : 3
  return (
    <div className="space-y-6" data-testid="brawl-roster-configuration">
      <div className="space-y-3">
        <Label className="text-sm font-bold">{t('game.specialRoles')} (4)</Label>
        <div className={gridClass}>{choices.fixed.map(role => fixedSeat(role))}</div>
        {randomSlot('extra', choices.extraGodChoices)}
      </div>
      <div className="space-y-3">
        <Label className="text-sm font-medium">{t('game.villagers')} ({villagerCount})</Label>
        <div className={gridClass}>{Array.from({ length: villagerCount }, (_, index) => fixedSeat(`villager_${index}`))}</div>
      </div>
      <div className="border-t border-gray-200 dark:border-gray-700" />
      <div className="space-y-3">
        <Label className="text-sm font-bold">{t('game.evilFaction')} ({wolfCount})</Label>
        {randomSlot('wolf', choices.wolfChoices)}
        <div className={gridClass}>{Array.from({ length: wolfCount - 1 }, (_, index) => fixedSeat(index ? `werewolf_${index + 1}` : 'werewolf'))}</div>
      </div>
    </div>
  )
}
